import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ForgeInlineMessage, ForgeCircularProgress } from '@tylertech/forge-react';
import { AG_SOQL_EXPORT_QUERY_LIMIT } from 'common/visualizations/helpers/TableDataExportHelpers';
import { isTablePrintingImprovementForFiltersEnabled } from 'common/visualizations/helpers/VifSelectors';
import { type PREVIEW_STATUS } from './index';

interface PDFPreviewProps {
  pdfData: string | null;
  status: PREVIEW_STATUS;
}

const renderMessage = (theme: string, message: string, isLoading = false) => {
  return (
    <div className="message-container">
      <ForgeInlineMessage theme={theme}>
        <div className="message-wrapper">
          {isLoading ? <ForgeCircularProgress /> : null}
          {message}
        </div>
      </ForgeInlineMessage>
    </div>
  );
};

const formattedExportQueryLimit = new Intl.NumberFormat('en-US').format(AG_SOQL_EXPORT_QUERY_LIMIT);

const ERROR_MESSAGES = {
  GENERATING: 'Generating preview.',
  ERROR: 'Error generating preview. Please try again later.',
  LIMIT_EXCEEDED:
    'Unable to generate the PDF as it exceeds the current processing limit. Please add filters to render a more specific table or export data using a different format.',
  TOO_MANY_ROWS: `PDFs are limited to ${formattedExportQueryLimit} rows. Please add filters to render a more specific table or export data using a different format.`,
  NO_PREVIEW: 'No preview available',
};

export const PDFPreview: React.FC<PDFPreviewProps> = ({ pdfData, status }) => {
  const [iframeSrc, setIframeSrc] = useState<string | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);

  useEffect(() => {
    // Cleanup when component unmounts or pdfData changes
    return () => {
      if (pdfData && pdfData.startsWith('blob:')) {
        URL.revokeObjectURL(pdfData);
      }
    };
  }, [pdfData]);

  const attachIframe = useCallback(
    (iframeElement: HTMLIFrameElement | null) => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      setIframeSrc(null);
      if (!iframeElement || !pdfData) {
        return;
      }
      const loadWhenSized = () => {
        if (iframeElement.offsetWidth > 0) {
          setIframeSrc(pdfData);
          return true;
        }
        return false;
      };
      if (loadWhenSized()) {
        return;
      }
      const observer = new ResizeObserver(() => {
        if (loadWhenSized()) {
          observer.disconnect();
          observerRef.current = null;
        }
      });
      observer.observe(iframeElement);
      observerRef.current = observer;
    },
    [pdfData],
  );

  if (status === 'initializing' || status === 'loading' || status === 'working') {
    return renderMessage('info-primary', ERROR_MESSAGES.GENERATING, true);
  }

  if (status === 'error') {
    return renderMessage('danger', ERROR_MESSAGES.ERROR);
  }

  if (status === 'limitExceededError') {
    return renderMessage('danger', ERROR_MESSAGES.LIMIT_EXCEEDED);
  }

  if (status === 'tooManyRows') {
    return renderMessage('danger', ERROR_MESSAGES.TOO_MANY_ROWS);
  }

  if (!pdfData) {
    return renderMessage('danger', ERROR_MESSAGES.NO_PREVIEW);
  }

  if (!isTablePrintingImprovementForFiltersEnabled()) {
    return <iframe src={pdfData} width="100%" height="100%" style={{ border: 'none' }} title="PDF Preview" />;
  }

  return (
    <iframe
      ref={attachIframe}
      src={iframeSrc ? `${iframeSrc}#zoom=100` : undefined}
      width="100%"
      height="100%"
      style={{ border: 'none' }}
      title="PDF Preview"
    />
  );
};
